import React, { useState, useEffect } from 'react';
import './AdminStudentRegistration.css';
import { fetchWithAuth, isSuperAdmin, getBranchId } from '../../utils/auth';
import { API_URL } from '../../config';
import { listAcademicYears, importStudents } from './studentRegistrationApi';

const REQUIRED_COLUMNS = ['student_id', 'first_name', 'last_name', 'grade'];
const OPTIONAL_COLUMNS = [
  'phone', 'primary_email', 'secondary_email', 'mother_name', 'father_name',
  'date_of_birth', 'amount_paid', 'volunteer_role', 'section', 'teacher_email', 'notes',
];

const STATUS_LABELS = {
  created: 'Created', updated: 'Updated', grade_updated: 'Grade Updated', skipped: 'Skipped', error: 'Error',
};

const AdminStudentRegistrationImport = () => {
  const superAdmin = isSuperAdmin();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(getBranchId() || 1);

  const [years, setYears] = useState([]);
  const [yearId, setYearId] = useState(null);

  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { counts, results }

  useEffect(() => {
    if (superAdmin) {
      fetchWithAuth(`${API_URL}/api/admin/branches`)
        .then(r => r.json())
        .then(d => setBranches(d.branches || []))
        .catch(() => {});
    }
  }, [superAdmin]);

  useEffect(() => {
    listAcademicYears().then(({ ok, data }) => {
      if (ok) {
        setYears(data.academic_years || []);
        const current = (data.academic_years || []).find(y => y.is_current);
        setYearId(current ? current.id : (data.academic_years[0]?.id ?? null));
      }
    });
  }, []);

  const handleImport = async () => {
    if (!file) { setError('Choose a CSV file first.'); return; }
    setImporting(true);
    setError('');
    setResult(null);
    const { ok, data } = await importStudents(file, branchId, yearId);
    if (ok) {
      setResult(data);
    } else {
      setError(data.error || 'Import failed.');
    }
    setImporting(false);
  };

  return (
    <div className="faq-manager-container">
      <div className="faq-manager-header">
        <div>
          <h2>Import Students</h2>
          <p className="faq-manager-subtitle">Bulk-load a roster from a CSV for one branch and academic year.</p>
        </div>
        <div className="faq-manager-header-right">
          {superAdmin && branches.length > 0 && (
            <select value={branchId} onChange={e => setBranchId(Number(e.target.value))} className="branch-select">
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <select value={yearId || ''} onChange={e => setYearId(Number(e.target.value))} className="branch-select">
            {years.map(y => <option key={y.id} value={y.id}>{y.year}{y.is_current ? ' (current)' : ''}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="form-card">
        <h3 className="faq-form-title">CSV Format</h3>
        <p style={{ fontSize: '13px', color: '#64748b' }}>
          Required columns: <strong>{REQUIRED_COLUMNS.join(', ')}</strong> — <code>grade</code> must match an
          existing grade name exactly (case-insensitive).
        </p>
        <p style={{ fontSize: '13px', color: '#64748b' }}>
          Optional columns: {OPTIONAL_COLUMNS.join(', ')}. Leave <code>amount_paid</code> blank to charge the full
          year fee. A student already registered for the selected year is skipped, not duplicated — safe to
          re-upload the same file after fixing errors.
        </p>

        <div className="sr-inline-row" style={{ marginTop: '12px' }}>
          <input type="file" accept=".csv,text/csv" onChange={e => setFile(e.target.files?.[0] || null)} />
          <button className="btn-primary" onClick={handleImport} disabled={importing || !file}>
            {importing ? 'Importing…' : 'Import'}
          </button>
        </div>
      </div>

      {result && (
        <div className="form-card">
          <h3 className="faq-form-title">
            Results — {result.counts.created} created, {result.counts.updated} updated,{' '}
            {result.counts.grade_updated || 0} grade updated,{' '}
            {result.counts.skipped} skipped, {result.counts.error} errors
          </h3>
          <div className="sr-table-scroll">
            <table className="admin-table">
              <thead>
                <tr><th>Row</th><th>Student ID</th><th>Status</th><th>Message</th></tr>
              </thead>
              <tbody>
                {result.results.map(r => (
                  <tr key={r.row}>
                    <td>{r.row}</td>
                    <td>{r.student_id || '—'}</td>
                    <td>
                      <span className={`sr-payment-badge ${r.status === 'error' ? 'sr-status-pending' : r.status === 'skipped' ? 'sr-status-neutral' : r.status === 'grade_updated' ? 'sr-status-partial' : 'sr-status-paid'}`}>
                        {STATUS_LABELS[r.status]}
                      </span>
                    </td>
                    <td>{r.message || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStudentRegistrationImport;
